import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { User, LogOut, ShieldCheck, Music2, Menu, X, Sparkles, Gift, MessageCircle, Coins, Radio, ListMusic } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import NotificationBell from "./NotificationBell";

export default function Header() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [open, setOpen] = useState(false);
    const [credits, setCredits] = useState(null);

    useEffect(() => {
        if (!user || user === false) { setCredits(null); return; }
        api.get("/ai/me").then(({ data }) => setCredits(data)).catch(() => {});
    }, [user]);

    const links = [
        { to: "/feed", label: "Fil d'actu" },
        { to: "/producers", label: "Annuaire" },
        { to: "/opportunites", label: "Opportunités" },
        { to: "/talent", label: "Talent 🎤" },
        { to: "/playlist", label: "Playlist" },
        { to: "/beats", label: "MBOA BEAT" },
        { to: "/services", label: "Services" },
        { to: "/catalog", label: "Catalogue" },
        { to: "/become-artist", label: "Devenir pro" },
        { to: "/about", label: "À propos" },
    ];

    return (
        <header className="fixed top-0 inset-x-0 z-50 bg-[#0a0a0a]/85 backdrop-blur-xl border-b border-[#00d97e]/15 shadow-[0_4px_30px_rgba(0,217,126,0.08)]" data-testid="site-header">
            <div className="max-w-7xl mx-auto px-5 md:px-10 h-20 flex items-center justify-between gap-4">
                <Link to="/" className="flex items-center gap-2 shrink-0 group" data-testid="logo-link">
                    <img decoding="async" src="/mboa-zick-logo.png" alt="MBOAZICK" className="h-12 w-auto transition-transform group-hover:scale-105" />
                </Link>

                <nav className="hidden lg:flex items-center gap-1">
                    {links.map((l) => (
                        <NavLink
                            key={l.label}
                            to={l.to}
                            data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `relative px-3 py-2 text-sm font-medium transition-colors ${
                                    isActive
                                        ? "text-[#00d97e]"
                                        : "text-[#A8A8A8] hover:text-[#F4F4F4]"
                                }`
                            }
                        >
                            {({ isActive }) => (
                                <>
                                    {l.label}
                                    {isActive && (
                                        <span className="absolute left-3 right-3 -bottom-0.5 h-0.5 rounded-full bg-gradient-to-r from-[#00d97e] via-[#ffc83d] to-[#ff6b35]" />
                                    )}
                                </>
                            )}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-2 shrink-0">
                    <NotificationBell />
                    {user && user !== false && (
                        <Link
                            to="/me/credits"
                            className="relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-[#00d97e]/40 text-[#00d97e] hover:bg-[#00d97e]/10 transition-colors"
                            data-testid="header-credits-link"
                            title="Mes crédits"
                        >
                            <Coins className="w-3.5 h-3.5" strokeWidth={1.8} />
                            <span>{credits ? (credits.paid_credits + (credits.premium_credits || 0)) : "—"} cr</span>
                        </Link>
                    )}

                    {user && user !== false ? (
                        <div className="hidden md:flex items-center gap-2">
                            {["artist", "provider", "organizer", "media"].includes(user.role) && (
                                <Link
                                    to="/ai-studio"
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-full transition-colors"
                                    data-testid="ai-studio-link"
                                >
                                    <Sparkles className="w-3.5 h-3.5" strokeWidth={1.8} /> AI Studio
                                </Link>
                            )}
                            {user.role === "artist" && (
                                <Link
                                    to="/artist"
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[#262626] hover:border-[#F4F4F4] rounded-full transition-colors"
                                    data-testid="artist-dashboard-link"
                                >
                                    <Music2 className="w-3.5 h-3.5" strokeWidth={1.8} /> Studio
                                </Link>
                            )}
                            {["artist", "provider", "organizer", "media"].includes(user.role) && (
                                <Link
                                    to="/me/gifts"
                                    className="hidden lg:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[#FF3D77]/40 text-[#FF3D77] hover:bg-[#FF3D77]/10 rounded-full transition-colors"
                                    data-testid="my-gifts-link"
                                    title="Mes cadeaux reçus"
                                >
                                    <Gift className="w-3.5 h-3.5" strokeWidth={1.8} /> Cadeaux
                                </Link>
                            )}
                            {user.role === "artist" && (
                                <Link
                                    to="/me/stream-earnings"
                                    className="hidden lg:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[#00c0ff]/40 text-[#00c0ff] hover:bg-[#00c0ff]/10 rounded-full transition-colors"
                                    data-testid="my-stream-earnings-link"
                                    title="Mes écoutes rémunérées"
                                >
                                    <Radio className="w-3.5 h-3.5" strokeWidth={1.8} /> Écoutes
                                </Link>
                            )}
                            <Link
                                to="/me/messages"
                                className="hidden lg:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[#00d97e]/40 text-[#00d97e] hover:bg-[#00d97e]/10 rounded-full transition-colors"
                                data-testid="messages-link"
                                title="Messagerie"
                            >
                                <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.8} /> Messages
                            </Link>
                            {user.role === "admin" && (
                                <Link
                                    to="/admin"
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-full transition-colors"
                                    data-testid="admin-link"
                                >
                                    <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.8} /> Admin
                                </Link>
                            )}
                            <Link
                                to="/me/playlists"
                                className="p-2 hover:bg-[#1c1c1c] rounded-full transition-colors"
                                data-testid="playlists-link"
                                title="Mes playlists"
                                aria-label="Mes playlists"
                            >
                                <ListMusic className="w-5 h-5" strokeWidth={1.5} />
                            </Link>
                            <Link
                                to="/account"
                                className="p-2 hover:bg-[#1c1c1c] rounded-full transition-colors"
                                data-testid="account-link"
                            >
                                <User className="w-5 h-5" strokeWidth={1.5} />
                            </Link>
                            <button
                                onClick={() => { logout(); nav("/"); }}
                                className="p-2 hover:bg-[#1c1c1c] rounded-full transition-colors"
                                data-testid="logout-button"
                                title="Se déconnecter"
                            >
                                <LogOut className="w-5 h-5" strokeWidth={1.5} />
                            </button>
                        </div>
                    ) : (
                        <div className="hidden md:flex items-center gap-2">
                            <Link to="/login" className="text-sm text-[#A8A8A8] hover:text-[#F4F4F4] px-3 py-1.5" data-testid="login-link">
                                Connexion
                            </Link>
                            <Link to="/register" className="text-sm bg-[#00A86B] hover:bg-[#00C97E] text-[#0F0F0F] font-medium px-4 py-2 rounded-full" data-testid="register-link">
                                S&apos;inscrire
                            </Link>
                        </div>
                    )}

                    <button onClick={() => setOpen((v) => !v)} className="lg:hidden p-2" data-testid="mobile-menu-toggle" aria-label="Menu">
                        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="lg:hidden border-t border-[#262626] bg-[#0F0F0F]/95 backdrop-blur-xl" data-testid="mobile-menu">
                    <div className="px-5 py-4 space-y-1">
                        {links.map((l) => (
                            <Link key={l.label} to={l.to} onClick={() => setOpen(false)} className="block px-3 py-2.5 rounded-lg text-sm text-[#A8A8A8] hover:bg-[#1c1c1c] hover:text-[#F4F4F4]">
                                {l.label}
                            </Link>
                        ))}
                        {user && user !== false && (
                            <div className="pt-3 mt-3 border-t border-[#262626] grid grid-cols-2 gap-2" data-testid="mobile-menu-fan">
                                <Link to="/account" onClick={() => setOpen(false)} className="inline-flex items-center justify-center gap-1.5 text-sm py-2.5 border border-[#262626] rounded-full"><User className="w-4 h-4" /> Mon espace</Link>
                                <Link to="/me/playlists" onClick={() => setOpen(false)} className="inline-flex items-center justify-center gap-1.5 text-sm py-2.5 border border-[#00d97e]/40 text-[#00d97e] rounded-full" data-testid="mobile-menu-playlists"><ListMusic className="w-4 h-4" /> Mes playlists</Link>
                            </div>
                        )}
                        <div className="pt-3 mt-3 border-t border-[#262626] flex gap-2">
                            {user && user !== false ? (
                                <>
                                    {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="flex-1 text-center text-sm py-2 border border-[#D4AF37]/40 text-[#D4AF37] rounded-full">Admin</Link>}
                                    {user.role === "artist" && <Link to="/artist" onClick={() => setOpen(false)} className="flex-1 text-center text-sm py-2 border border-[#262626] rounded-full">Studio</Link>}
                                    <button onClick={() => { setOpen(false); logout(); nav("/"); }} className="flex-1 text-sm py-2 bg-[#1c1c1c] rounded-full">Se déconnecter</button>
                                </>
                            ) : (
                                <>
                                    <Link to="/login" onClick={() => setOpen(false)} className="flex-1 text-center text-sm py-2 border border-[#262626] rounded-full">Connexion</Link>
                                    <Link to="/register" onClick={() => setOpen(false)} className="flex-1 text-center text-sm py-2 bg-[#00A86B] text-[#0F0F0F] rounded-full font-medium">S&apos;inscrire</Link>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
